/**
 * Browser storage keys, in one place.
 *
 * Two pages share the league password: /league gates the live tables on it and
 * /prizes accepts it as the stronger of its two unlocks. Entering it on either
 * should open the other, which only works if both agree on the key — so it is
 * defined once here rather than as a private constant in each file.
 *
 * localStorage, and it used to be sessionStorage.
 *
 * The original reasoning was that a shared WhatsApp secret should not outlive
 * the tab on a borrowed laptop. In practice almost nobody reads this site on a
 * borrowed laptop and everybody closes tabs, so the cost was paid by all 200
 * members — retyping a password from a WhatsApp message every single visit —
 * to protect against a case that mostly doesn't happen. It is the identical
 * argument the prize email lost below, and it loses here for the same reason.
 *
 * What this is NOT is a weakening of the gate. The password is checked on the
 * server by getStandings and getPrizeStructure on every single call; this only
 * decides how long the browser remembers what to send. Someone with the
 * password already has the password.
 *
 * Anyone who does want it forgotten has "Forget this device" on /league.
 */
export const LEAGUE_PW_KEY = 'rab_league_pw';

/**
 * The address someone registered with, kept so /prizes can prove they did.
 *
 * localStorage, NOT sessionStorage — and that distinction is the whole point.
 * sessionStorage dies when the tab closes, so a person who registered on
 * Monday came back on Tuesday locked out, with no way in but to hand over an
 * email a second time. Persisting it on their own device means the form is
 * asked for once, ever.
 *
 * Not a credential: it is the user's own address, and the server still checks
 * it against a real submitted form before it unlocks anything.
 */
export const PRIZE_EMAIL_KEY = 'rab_prize_email';

/**
 * The WhatsApp invite, cached from a server response so the footer can link
 * straight to it.
 *
 * The invite URL must never be in the bundle (invariant 1) — every route
 * serves the same JS, so a hardcoded href would publish the group to anyone
 * who viewed source. This is the other way round: getStandings hands the URL
 * to someone who already gave the password, and we keep that answer for the
 * rest of the tab so the footer's "The WhatsApp group" is a real link rather
 * than a second trip through /league. Nothing is fetched to fill it; if it is
 * empty the footer falls back to pointing at /league, which is where the
 * password gate lives.
 *
 * localStorage for the same reason as the password it is derived from — and it
 * has to match: an invite that expired with the tab while the tables stayed
 * unlocked would leave the footer pointing at a gate the reader is already
 * through.
 */
export const WHATSAPP_INVITE_KEY = 'rab_whatsapp_invite';

/**
 * Storage events only fire in *other* tabs, so the footer needs its own nudge
 * to notice that the page it is wrapped around just unlocked.
 */
export const WHATSAPP_INVITE_EVENT = 'rab:whatsapp-invite';

export function setWhatsappInvite(url: string | null) {
  writeStored(WHATSAPP_INVITE_KEY, url);
  window.dispatchEvent(new Event(WHATSAPP_INVITE_EVENT));
}

export function getWhatsappInvite(): string | null {
  return readStored(WHATSAPP_INVITE_KEY);
}

/* -------------------------------------------------------------------------- */
/* The league password                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Read the remembered password.
 *
 * Falls back to sessionStorage once, for anyone who unlocked before this
 * changed and still has the tab open — without it, shipping this would sign
 * out everyone currently reading, which is a strange way to deliver "you
 * should not have to type this again". Found values are promoted to
 * localStorage so the fallback is needed exactly once per person.
 */
export function getLeaguePassword(): string | null {
  const stored = readStored(LEAGUE_PW_KEY);
  if (stored) return stored;

  const legacy = readSession(LEAGUE_PW_KEY);
  if (legacy) {
    writeStored(LEAGUE_PW_KEY, legacy);
    removeSession(LEAGUE_PW_KEY);
    return legacy;
  }
  return null;
}

/** Remember the password. Only ever called after the server has accepted it. */
export function setLeaguePassword(password: string) {
  writeStored(LEAGUE_PW_KEY, password);
}

/** Forget it — a wrong/stale password, or someone using the /league control. */
export function clearLeaguePassword() {
  writeStored(LEAGUE_PW_KEY, null);
  removeSession(LEAGUE_PW_KEY);
}

/* -------------------------------------------------------------------------- */
/* Storage access                                                              */
/* -------------------------------------------------------------------------- */

/*
 * Every access is wrapped.
 *
 * Safari in private browsing, and any browser set to block site data, throws on
 * localStorage rather than returning null. Unwrapped, that throw happens during
 * render on /league and takes the whole page down — for a convenience feature.
 * Failing to remember is a fine outcome; failing to render is not.
 */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* Not remembering is survivable. */
  }
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeSession(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* Ditto. */
  }
}
